// Proyectos — Banco de Proyectos. El trámite vive SOLO en Red de Proyectos
// (INTEGRACION_COOPNET_INSCRIPCION.md): CoopNet es la puerta de
// entrada de la cooperativa y muestra el estado de sus proyectos.
//
//   • Inscribir: quien tenga el rol RadicadorProyectos en la cooperativa pulsa
//     «Inscribir proyecto». El backend de CoopNet pide a Red de Proyectos un pase de un solo
//     uso y aquí se abre la ventana de inscripción de Red de Proyectos en un popup (iframe).
//     La ventana avisa por postMessage; solo se aceptan mensajes del origen de
//     Red de Proyectos (REACT_APP_RED_PROYECTOS_WEB_URL).
//   • Ver: lista y detalle de solo lectura, leídos de Red de Proyectos por el backend de
//     CoopNet. Se ven TODOS los proyectos de la cooperativa, radicados desde
//     CoopNet o directamente en Red de Proyectos.
//
// Por nivel:
//   cooperativa → su cooperativa (con el botón si tiene el rol);
//   afiliado    → los de su cooperativa, solo lectura;
//   federación  → todos los de su federación, o los de una de sus cooperativas;
//   global      → se elige una federación (y opcionalmente una cooperativa).
//
// Reemplaza el trámite propio de CoopNet (radicar / decidir / avances / cerrar),
// cuyos endpoints quedaron obsoletos en el backend.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, IconButton, InputLabel, Link, MenuItem, Select, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Add as AddIcon, Assignment as AssignmentIcon, Close as CloseIcon, Pending as PendingIcon,
  ReportProblem as ReportProblemIcon, TaskAlt as TaskAltIcon,
} from '@mui/icons-material';
import TablaDatos from '../components/TablaDatos';
import useCargar from '../hooks/useCargar';
import useCooperativaOperativa from '../hooks/useCooperativaOperativa';
import useRedReal from '../hooks/useRedReal';
import { useAuth } from '../context/AuthContext';
import { inscripcionProyectosApi } from '../services/coopnetApi';
import { fecha, fmt, moneda } from '../utils/formato';

const RED_PROYECTOS_WEB = process.env.REACT_APP_RED_PROYECTOS_WEB_URL || '';
const ORIGEN_RED_PROYECTOS = (() => {
  try {
    return new URL(RED_PROYECTOS_WEB).origin;
  } catch {
    return '';
  }
})();

// Estados del trámite (contrato §5.9). El TEXTO siempre es el estadoEtiqueta que manda
// Red de Proyectos; aquí solo el color del chip y las etiquetas del filtro.
const ESTADOS = {
  pending: ['Radicado', 'default'],
  in_review: ['Radicado en revisión', 'warning'],
  devuelto: ['Devuelto', 'error'],
  admitido: ['Admitido', 'info'],
  en_evaluacion: ['En evaluación', 'warning'],
  solicita_ajustes: ['Ajustes solicitados', 'warning'],
  en_decision: ['En decisión', 'warning'],
  aprobado: ['Aprobado', 'success'],
  aprobado_condiciones: ['Aprobado con condiciones', 'success'],
  remitido_federacion: ['Remitido a federación', 'info'],
  en_reposicion: ['En reposición', 'warning'],
  formulated: ['En ejecución', 'primary'],
  rejected: ['Rechazado', 'error'],
  archivado: ['Archivado', 'default'],
};
const CERRADOS = ['rejected', 'archivado'];
const APROBADOS = ['aprobado', 'aprobado_condiciones', 'remitido_federacion', 'formulated'];

const EstadoChip = ({ p }) => (
  <Chip size="small" color={ESTADOS[p.estado]?.[1] || 'default'} label={p.estadoEtiqueta || ESTADOS[p.estado]?.[0] || p.estado} />
);

/** Mensaje de error para el usuario; en sesión vencida ofrece volver a entrar. */
const AvisoError = ({ error, onSalir }) => {
  if (!error) return null;
  const vencida = error.codigo === 'usuario_token_expirado';
  return (
    <Alert
      severity="error"
      sx={{ mb: 2 }}
      action={vencida ? <Button color="inherit" size="small" onClick={onSalir}>Volver a iniciar sesión</Button> : undefined}
    >
      {vencida ? 'Tu sesión venció. Vuelve a iniciar sesión para continuar.' : error.mensaje}
    </Alert>
  );
};
const aError = (e) => (e ? { codigo: e.body?.codigo, mensaje: e.message || 'Ocurrió un error.' } : null);

// ───────────────────────── Ventana de inscripción (Red de Proyectos en popup) ─────────────────────────
function InscripcionDialog({ cooperativaId, abierto, onCerrar, onRadicada }) {
  const theme = useTheme();
  const movil = useMediaQuery(theme.breakpoints.down('sm'));
  const { logout } = useAuth();
  const [url, setUrl] = useState('');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const renovaciones = useRef(0);

  const pedirPase = useCallback(async () => {
    setCargando(true);
    setError(null);
    setUrl('');
    try {
      const r = await inscripcionProyectosApi.pedirPase(cooperativaId);
      setUrl(r?.url || '');
      if (!r?.url) setError({ mensaje: 'Red de Proyectos no devolvió la dirección de la ventana.' });
    } catch (e) {
      setError(aError(e));
      setCargando(false);
    }
  }, [cooperativaId]);

  // Un pase nuevo cada vez que se abre el popup (no se guarda).
  useEffect(() => {
    if (!abierto) return;
    renovaciones.current = 0;
    pedirPase();
  }, [abierto, pedirPase]);

  // Mensajes de la ventana: SOLO del origen de Red de Proyectos.
  useEffect(() => {
    if (!abierto) return undefined;
    const alRecibir = (event) => {
      if (!ORIGEN_RED_PROYECTOS || event.origin !== ORIGEN_RED_PROYECTOS) return;
      const m = event.data;
      if (!m || m.origen !== 'red-de-proyectos') return;
      switch (m.tipo) {
        case 'inscripcion.lista':
          setCargando(false);
          break;
        case 'inscripcion.radicada':
          onRadicada?.(m.datos || {});
          break;
        case 'inscripcion.cancelada':
          onCerrar();
          break;
        case 'inscripcion.sesion_expirada':
          // Un solo reintento automático con un pase nuevo; si vuelve a vencer, se avisa.
          if (renovaciones.current < 1) {
            renovaciones.current += 1;
            pedirPase();
          } else {
            setUrl('');
            setCargando(false);
            setError({ mensaje: 'La sesión de la ventana de inscripción venció. Cierra y vuelve a intentarlo.' });
          }
          break;
        case 'inscripcion.error':
          setUrl('');
          setCargando(false);
          setError({ codigo: m.datos?.codigo, mensaje: m.datos?.mensaje || 'Red de Proyectos no pudo continuar con la inscripción.' });
          break;
        default:
          break;
      }
    };
    window.addEventListener('message', alRecibir);
    return () => window.removeEventListener('message', alRecibir);
  }, [abierto, onCerrar, onRadicada, pedirPase]);

  return (
    <Dialog
      open={abierto}
      onClose={onCerrar}
      fullScreen={movil}
      fullWidth
      maxWidth="xl"
      PaperProps={{ sx: movil ? {} : { height: '90vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>Inscripción de proyecto</Typography>
        <IconButton aria-label="Cerrar" onClick={onCerrar}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, position: 'relative', display: 'flex' }}>
        {error && (
          <Box sx={{ p: 3, width: '100%' }}>
            <AvisoError error={error} onSalir={logout} />
          </Box>
        )}
        {!error && cargando && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, bgcolor: 'background.paper', zIndex: 1 }}>
            <CircularProgress size={28} />
            <Typography color="text.secondary">Abriendo Red de Proyectos…</Typography>
          </Box>
        )}
        {!error && url && (
          <iframe
            key={url}
            src={url}
            title="Inscripción de proyecto"
            width="100%"
            height="100%"
            style={{ border: 0, flex: 1, minHeight: movil ? '100%' : 0 }}
            allow="clipboard-write"
            // Respaldo: si la ventana no llega a avisar «lista» (p. ej. el navegador la bloqueó por
            // frame-ancestors), no se deja el indicador de carga encima para siempre.
            onLoad={() => setCargando(false)}
            sandbox="allow-scripts allow-same-origin allow-forms allow-downloads allow-popups"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────────── Detalle (solo lectura) ─────────────────────────
function DetalleDialog({ id, onCerrar, onSalir }) {
  const [error, setError] = useState(null);
  const { datos: p, cargando } = useCargar(async () => {
    if (!id) return null;
    setError(null);
    try {
      return await inscripcionProyectosApi.detalle(id);
    } catch (e) {
      setError(aError(e));
      return null;
    }
  }, [id]);

  const dato = (titulo, valor) => (
    <Box>
      <Typography variant="caption" color="text.secondary">{titulo}</Typography>
      <Typography>{valor ?? '—'}</Typography>
    </Box>
  );
  const s = p?.subsanacion;

  return (
    <Dialog open={Boolean(id)} onClose={onCerrar} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{p?.nombre || 'Proyecto'}</DialogTitle>
      <DialogContent dividers>
        <AvisoError error={error} onSalir={onSalir} />
        {cargando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : p ? (
          <>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
              <EstadoChip p={p} />
              {p.lineaComite?.nombre && <Chip size="small" variant="outlined" label={p.lineaComite.nombre} />}
              {p.nivelTramite && <Chip size="small" variant="outlined" label={`Nivel: ${p.nivelTramite}`} />}
            </Box>

            {s && (
              <Alert
                severity={s.vencido ? 'error' : 'warning'}
                sx={{ mb: 2 }}
                action={s.enlace ? <Button color="inherit" size="small" href={s.enlace} target="_blank" rel="noopener noreferrer">Subsanar</Button> : undefined}
              >
                {s.vencido
                  ? `El plazo para subsanar venció el ${fecha(s.fechaLimite)}.`
                  : `Proyecto devuelto: hay plazo hasta el ${fecha(s.fechaLimite)} (${s.diasHabilesRestantes} días hábiles) para cargar los documentos corregidos.`}
              </Alert>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
              {dato('Radicado', p.radicadoNumero)}
              {dato('Fecha de radicación', fecha(p.fechaRadicacion))}
              {dato('Presupuesto', p.presupuestoCop != null ? moneda(p.presupuestoCop) : null)}
              {dato('Duración', p.duracionMeses ? `${p.duracionMeses} meses` : null)}
              {dato('Inscrito por', p.inscritoPor?.nombre)}
              {dato('Última actualización', fecha(p.actualizado))}
            </Box>
            {p.descripcion && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Descripción</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>{p.descripcion}</Typography>
              </Box>
            )}

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>Documentos</Typography>
            <TablaDatos
              filas={p.documentos || []}
              vacio="Sin documentos registrados."
              columnas={[
                { titulo: 'Documento', render: (d) => d.nombreTipo || d.tipo },
                { titulo: 'Archivo', render: (d) => d.archivo || '—' },
                { titulo: 'Versión', render: (d) => d.version ?? '—' },
                { titulo: 'Estado', render: (d) => d.estado || '—' },
              ]}
            />

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>Historial</Typography>
            <TablaDatos
              filas={p.historial || []}
              vacio="Sin movimientos registrados."
              columnas={[
                { titulo: 'Fecha', render: (h) => fecha(h.fecha) },
                { titulo: 'Evento', render: (h) => h.evento },
                { titulo: 'Descripción', render: (h) => h.descripcion || '—' },
              ]}
            />
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        {RED_PROYECTOS_WEB && (
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, pl: 2 }}>
            El trámite se lleva en <Link href={RED_PROYECTOS_WEB} target="_blank" rel="noopener noreferrer">Red de Proyectos</Link>.
          </Typography>
        )}
        <Button onClick={onCerrar}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

// ───────────────────────── Página ─────────────────────────
const Proyectos = () => {
  const { user, contexto, hasRole, logout } = useAuth();
  const red = useRedReal();
  const operativa = useCooperativaOperativa();

  const esCooperativa = user?.nivel === 'cooperativa';
  const esAfiliado = user?.nivel === 'afiliado';
  const esFederacion = user?.nivel === 'federacion';
  const esGlobal = !esCooperativa && !esAfiliado && !esFederacion;

  // Federación: fija en su vista; elegida en la global.
  const [fedElegida, setFedElegida] = useState('');
  const federacionId = esFederacion ? contexto?.federacion?.id || '' : fedElegida;
  // Cooperativa: fija en cooperativa/afiliado; opcional en federación y global.
  const [coopElegida, setCoopElegida] = useState('');
  const cooperativaId = esCooperativa || esAfiliado ? operativa.cooperativaId || '' : coopElegida;

  const cooperativasDeLaFederacion = useMemo(
    () => red.cooperativas
      .filter((c) => c.activa !== false && c.federacionId === federacionId)
      .sort((a, b) => a.nombre.localeCompare(b.nombre)),
    [red.cooperativas, federacionId]
  );
  const nombrePorEcolab = useMemo(
    () => new Map(red.cooperativas.filter((c) => c.ecolabId != null).map((c) => [c.ecolabId, c.nombre])),
    [red.cooperativas]
  );

  const [filtroEstado, setFiltroEstado] = useState('');
  const [inscribiendo, setInscribiendo] = useState(false);
  const [detalleId, setDetalleId] = useState(null);
  const [aviso, setAviso] = useState('');
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);

  const filtro = useMemo(() => {
    if (cooperativaId) return { cooperativaId };
    if (federacionId && (esFederacion || esGlobal)) return { federacionId };
    return null;
  }, [cooperativaId, federacionId, esFederacion, esGlobal]);
  const porFederacion = Boolean(filtro?.federacionId);

  const { datos, cargando } = useCargar(async () => {
    if (!filtro) return null;
    setError(null);
    try {
      return await inscripcionProyectosApi.listar({ ...filtro, estado: filtroEstado || undefined, tamano: 100 });
    } catch (e) {
      setError(aError(e));
      return null;
    }
  }, [JSON.stringify(filtro), filtroEstado, tick]);

  const items = useMemo(() => datos?.items || [], [datos]);
  const stats = useMemo(() => ({
    total: datos?.total ?? items.length,
    enTramite: items.filter((p) => !CERRADOS.includes(p.estado) && !APROBADOS.includes(p.estado)).length,
    aprobados: items.filter((p) => APROBADOS.includes(p.estado)).length,
    devueltos: items.filter((p) => p.estado === 'devuelto').length,
  }), [datos, items]);

  // El botón: vista de cooperativa y rol RadicadorProyectos en ESA cooperativa (el backend lo vuelve a verificar).
  const puedeInscribir = esCooperativa && Boolean(cooperativaId) && hasRole('RadicadorProyectos', cooperativaId);

  const alRadicar = useCallback((d) => {
    setInscribiendo(false);
    setAviso(`Proyecto inscrito${d.radicadoNumero ? ` con el radicado ${d.radicadoNumero}` : ''}${d.nombre ? `: ${d.nombre}` : ''}.`);
    setTick((n) => n + 1);
  }, []);
  const cerrarInscripcion = useCallback(() => setInscribiendo(false), []);

  const columnas = [
    { titulo: 'Radicado', render: (p) => p.radicadoNumero || '—' },
    { titulo: 'Proyecto', render: (p) => p.nombre },
    ...(porFederacion
      ? [{ titulo: 'Cooperativa', render: (p) => p.cooperativa?.nombre || nombrePorEcolab.get(p.cooperativaEcolabId) || '—' }]
      : []),
    { titulo: 'Línea', render: (p) => p.lineaComite?.nombre || '—' },
    { titulo: 'Estado', render: (p) => <EstadoChip p={p} /> },
    { titulo: 'Radicación', render: (p) => fecha(p.fechaRadicacion) },
    { titulo: 'Inscrito por', render: (p) => p.inscritoPor?.nombre || '—' },
    { titulo: '', render: (p) => <Button size="small" onClick={() => setDetalleId(p.id)}>Ver</Button> },
  ];

  const sinEntidad = esCooperativa || esAfiliado
    ? 'Tu usuario aún no está asociado a una cooperativa.'
    : esFederacion
    ? 'Tu usuario aún no está asociado a una federación.'
    : 'Elige una federación para ver sus proyectos.';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {esAfiliado ? 'Proyectos de mi cooperativa' : 'Banco de Proyectos'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Proyectos inscritos en Red de Proyectos y su estado en el trámite
          </Typography>
        </Box>
        {puedeInscribir && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setInscribiendo(true)}>
            Inscribir proyecto
          </Button>
        )}
      </Box>

      {(esGlobal || esFederacion) && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          {esGlobal && (
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>Federación</InputLabel>
              <Select
                label="Federación"
                value={fedElegida}
                onChange={(e) => {
                  setFedElegida(e.target.value);
                  setCoopElegida('');
                }}
              >
                {red.federaciones.map((f) => (
                  <MenuItem key={f.id} value={f.id}>{f.nombre}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {federacionId && (
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>Cooperativa</InputLabel>
              <Select label="Cooperativa" value={coopElegida} onChange={(e) => setCoopElegida(e.target.value)}>
                <MenuItem value="">Todas las de la federación</MenuItem>
                {cooperativasDeLaFederacion.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      )}

      {aviso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAviso('')}>{aviso}</Alert>}
      <AvisoError error={error} onSalir={logout} />

      {!filtro ? (
        <Alert severity="info">{sinEntidad}</Alert>
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
            {[
              ['Proyectos', fmt(stats.total), AssignmentIcon, '#6A1B9A'],
              ['En trámite', fmt(stats.enTramite), PendingIcon, '#E65100'],
              ['Aprobados', fmt(stats.aprobados), TaskAltIcon, '#2E7D32'],
              ['Devueltos', fmt(stats.devueltos), ReportProblemIcon, '#C62828'],
            ].map(([t, v, Icono, color]) => (
              <Card key={t}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${color}1A`, color, display: 'flex' }}><Icono /></Box>
                  <Box><Typography variant="h6" fontWeight="bold" sx={{ color }}>{v}</Typography><Typography variant="body2">{t}</Typography></Box>
                </CardContent>
              </Card>
            ))}
          </Box>

          <FormControl size="small" sx={{ minWidth: 220, mb: 2 }}>
            <InputLabel>Estado</InputLabel>
            <Select label="Estado" value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(ESTADOS).map(([valor, [etiqueta]]) => (
                <MenuItem key={valor} value={valor}>{etiqueta}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {cargando ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : (
            <TablaDatos filas={items} vacio="No hay proyectos inscritos con este filtro." columnas={columnas} />
          )}
        </>
      )}

      {puedeInscribir && (
        <InscripcionDialog
          cooperativaId={cooperativaId}
          abierto={inscribiendo}
          onCerrar={cerrarInscripcion}
          onRadicada={alRadicar}
        />
      )}
      <DetalleDialog id={detalleId} onCerrar={() => setDetalleId(null)} onSalir={logout} />
    </Box>
  );
};

export default Proyectos;
